import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

interface LandingPageProps {
  // Hero Section
  headline: string;
  subheadline: string;
  heroImage?: string;
  ctaText?: string;
  ctaLink?: string;
  
  // Value Proposition
  valueProps: string[];
  
  // Features/Benefits
  features: Array<{
    title: string;
    description: string;
    icon?: React.ReactNode;
  }>;
  
  // Social Proof
  stats?: Array<{
    value: string;
    label: string;
  }>;
  
  // CTA Section
  ctaHeadline?: string;
  ctaDescription?: string;
  
  // Brand Colors
  primaryColor?: string;
  accentColor?: string;
}

const LandingPageTemplate: React.FC<LandingPageProps> = ({
  headline,
  subheadline,
  heroImage,
  ctaText = 'Get a Quote',
  ctaLink = '#contact',
  valueProps,
  features,
  stats,
  ctaHeadline = 'Ready to Transform Your Infrastructure?',
  ctaDescription = 'Contact our experts today for a quote and customized proposal.',
  primaryColor = '#1976d2',
  accentColor = '#0d47a1',
}) => {
  const handleCTAClick = () => {
    if (ctaLink.startsWith('#')) {
      const element = document.getElementById(ctaLink.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = ctaLink;
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '3rem' } }}
              >
                {headline}
              </Typography>
              <Typography
                variant="h5"
                paragraph
                sx={{ mb: 4, fontSize: { xs: '1.1rem', md: '1.5rem' } }}
              >
                {subheadline}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleCTAClick}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  bgcolor: 'white',
                  color: primaryColor,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  },
                }}
              >
                {ctaText}
              </Button>
            </Grid>
            {heroImage && (
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src={heroImage}
                  alt="Hero"
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Value Propositions */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Why Choose This Solution?
          </Typography>
          <Grid container spacing={3}>
            {valueProps.map((prop, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                  <CardContent>
                    <CheckIcon
                      sx={{ fontSize: 48, color: primaryColor, mb: 2 }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {prop}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features & Benefits */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Key Features & Benefits
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 4, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    {feature.icon && (
                      <Box sx={{ color: primaryColor, mr: 2, fontSize: 40 }}>
                        {feature.icon}
                      </Box>
                    )}
                    <Box>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      {stats && stats.length > 0 && (
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
            color: 'white',
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              {stats.map((stat, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="h2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="h6">{stat.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Final CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="md">
          <Paper sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {ctaHeadline}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{ fontSize: '1.1rem', mb: 4 }}
            >
              {ctaDescription}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCTAClick}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  bgcolor: primaryColor,
                }}
              >
                {ctaText}
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PhoneIcon />}
                href="tel:+1234567890"
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  borderColor: primaryColor,
                  color: primaryColor,
                }}
              >
                Call Now
              </Button>
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ color: primaryColor }} />
                <Typography>+1 (234) 567-890</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: primaryColor }} />
                <Typography>info@tncosoft.com</Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPageTemplate;
